import { apiService } from 'services/service';

export const DEFAULT_NGN_RATE = 0.00068;

export const NGN_FALLBACK = {
    code: 'NGN',
    symbol: 'NGN',
    name: 'Nigerian Naira',
    sell: DEFAULT_NGN_RATE,
    buy: DEFAULT_NGN_RATE,
    is_base: false,
    hasRate: true,
    exchange_rate: DEFAULT_NGN_RATE,
};

const STORAGE_KEY = 'obana_selected_currency';

const saveToStorage = (currency) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(currency));
    } catch {}
};

export const loadFromStorage = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
    } catch {
        return null;
    }
};

export const setCurrencyValue = (v) => ({ type: 'SET_CURRENCY', payload: { currency: v } });
export const setCurrenciesRates = (r) => ({ type: 'SET_CURRENCIES_RATES', payload: r });

export const selectCurrency = (currency) => (dispatch) => {
    dispatch(setCurrenciesRates(currency));
    dispatch(setCurrencyValue(currency.symbol));
    saveToStorage(currency);
};

export const fetchCurrencies = () => async (dispatch, getState) => {
    dispatch({ type: 'GET_CURRENCIES', payload: true });

    try {
        const { data } = await apiService.get('/requests/zoho-book/get-currencies');
        const raw = data?.currencies ?? data?.data?.currencies ?? [];
        const rawArray = Array.isArray(raw) ? raw : [];

        const mapped = rawArray.map((c) => {
            const code = c.currency_code?.toUpperCase() ?? '';
            const symbol = c.currency_symbol ?? code;
            const zohoRate = parseFloat(c.exchange_rate) || 0;

            const sell = c.is_base_currency ? 1 : zohoRate;

            return {
                code,
                symbol,
                name: c.currency_name ?? code,
                sell,
                buy: sell,
                is_base: !!c.is_base_currency,
                hasRate: c.is_base_currency || zohoRate > 0,
                currency_id: c.currency_id,
                currency_name: c.currency_name,
                exchange_rate: zohoRate,
            };
        });

        const hasUSD = mapped.some((c) => c.code === 'USD');
        const currencies = hasUSD
            ? mapped
            : [
                  {
                      code: 'USD',
                      symbol: '$',
                      name: 'US Dollar',
                      sell: 1,
                      buy: 1,
                      is_base: true,
                      hasRate: true,
                      exchange_rate: 0,
                  },
                  ...mapped,
              ];

        dispatch({ type: 'SET_CURRENCIES', payload: currencies });

        const saved = loadFromStorage();
        const currentCode = getState().currencyReducer?.currenciesRates?.symbol?.toUpperCase();
        const codeToRestore = saved?.symbol?.toUpperCase() ?? currentCode;

        if (codeToRestore) {
            const fresh = currencies.find(
                (c) =>
                    (c.symbol?.toUpperCase() ?? c.code?.toUpperCase()) === codeToRestore &&
                    c.hasRate,
            );
            if (fresh) {
                dispatch(setCurrenciesRates(fresh));
                dispatch(setCurrencyValue(fresh.symbol));
                saveToStorage(fresh);
                return;
            }
        }

        const livNGN = currencies.find((c) => c.code === 'NGN' || c.symbol === 'NGN');
        const defaultCurrency = livNGN ?? NGN_FALLBACK;
        dispatch(setCurrenciesRates(defaultCurrency));
        dispatch(setCurrencyValue(defaultCurrency.symbol));
        saveToStorage(defaultCurrency);
    } catch (error) {
        console.error('fetchCurrencies error:', error);
        const saved = loadFromStorage();
        const fallback = saved ?? NGN_FALLBACK;
        dispatch({ type: 'SET_CURRENCIES', payload: [fallback] });
        dispatch(setCurrenciesRates(fallback));
        dispatch(setCurrencyValue(fallback.symbol));
        if (typeof error === 'string' && error?.toLowerCase()?.includes('session expired')) {
            window.location.href = '/logout';
        }
    } finally {
        dispatch({ type: 'GET_CURRENCIES', payload: false });
    }
};

export const setCurrencyFromUserProfile = (currencyCode) => (dispatch, getState) => {
    if (!currencyCode) return;
    const currencies = getState().currencyReducer?.currencies ?? [];
    const match =
        currencies.find(
            (c) =>
                (c.symbol?.toUpperCase() === currencyCode?.toUpperCase() ||
                    c.code?.toUpperCase() === currencyCode?.toUpperCase()) &&
                c.hasRate,
        ) ??
        currencies.find((c) => c.code === 'NGN' || c.symbol === 'NGN') ??
        NGN_FALLBACK;

    dispatch(setCurrenciesRates(match));
    dispatch(setCurrencyValue(match.symbol));
    saveToStorage(match);
};
